/**
 * Simulation content — the other half of the white-label API.
 * Swap this file (or this export) to run a completely different scenario.
 *
 * Each ROUND must include:
 *   name, eyebrow, dur (minutes), max (max score excl. bomb), sit, brief,
 *   bomb, bombField { label, hint }, fields [{ id, label, hint }],
 *   steward [string], dims [string], bombDim string
 *
 * Optional per-round:
 *   priorities [{ n, t, v, tag }]  — displayed in Round 1
 *   options    [{ id, t, d, r }]   — displayed in Round 3
 */

import { applyBrandOverrides } from './brand.js';

export const TEAM_NAMES = [
  'Team 1','Team 2','Team 3','Team 4','Team 5',
  'Team 6','Team 7','Team 8','Team 9',
];

/** Maps scorer number → array of team names they score */
export const SCORER_TEAMS = {
  1: ['Team 1'],
  2: ['Team 2'],
  3: ['Team 3'],
  4: ['Team 4'],
  5: ['Team 5'],
  6: ['Team 6'],
  7: ['Team 7'],
  8: ['Team 8'],
  9: ['Team 9'],
};

const DEFAULT_BADGES = [
  { id: 'b1', label: 'Market First',      icon: '🎯', cls: 'badge-pill--1' },
  { id: 'b2', label: 'Speaks Up',         icon: '📢', cls: 'badge-pill--2' },
  { id: 'b3', label: 'Enterprise First',  icon: '🏛',  cls: 'badge-pill--3' },
  { id: 'b4', label: 'Sequenced Change',  icon: '🔀', cls: 'badge-pill--4' },
  { id: 'b5', label: 'Held The Line',     icon: '🛡',  cls: 'badge-pill--5' },
];

const DEFAULT_ROUNDS = [
  {
    name: 'Prioritise',
    eyebrow: 'Round 1 of 3 — MedVance Group',
    dur: 28,
    max: 9,
    sit: 'The external review findings have been delivered to the board. The board requires written confirmation by Friday of what this leadership team is committing to deliver by 30 June — and what it is explicitly not doing.\n\nWhat follows is a full picture of what is currently competing for the organisation\'s time and resource. Some of these are the urgent fixes the review demanded. Some are the long-term stewardship obligations that were already overdue before the review arrived. Some are commitments already in motion. Not all of them can be done to the standard required in the time available.\n\nMaking that call — clearly, honestly, and collectively — is what this brief asks of you.',
    brief: '<strong>Priority Declaration:</strong> (1) Commit fully to max 3 priorities. <br><br>(2) Manage at reduced capacity max 3 more. <br><br>(3) Stop or pause at least 1 — deferring is not stopping. <br><br><strong>What the board is watching for:</strong> The board wants to see two things: that you can prioritise — genuinely, not nominally — and that you can communicate that decision clearly to the people who need to hear it.',
    bomb: "People are filling the silence left by the leadership team. The Chief People Officer needs to know: what will you communicate to your teams — the priorities, the why, and the when. You have 3 minutes.",
    bombField: {
      label: 'Message to your people (CPO bomb drop response)',
      hint: ' ',
    },
    priorities: [
      { n: 1, t: 'APEX system upgrade — 6 months behind, needs full technology team for 4 weeks', v: 'Failing damages TGA/FDA trust. Delivering leaves nothing else.', tag: '' },
      { n: 2, t: 'Risk register clearance — 23 items, 11 with no formal owner', v: 'The external review specifically called this out. But who actually owns the work?', tag: '' },
      { n: 3, t: 'Staff survey response — engagement has dropped', v: 'Culture is what the external review says needs to change. But this takes leadership bandwidth.', tag: '' },
      { n: 4, t: 'Customer advisory forum — three major customers threatening media', v: "Responsive in the short term. But is this the CEO's job right now?", tag: '' },
      { n: 5, t: 'Project CLEAR — 6 weeks in, timeline already unrealistic', v: 'The board approved it. The business case was optimistic. Do you tell them now?', tag: '' },
      { n: 6, t: 'Board financial update — costs running 18% over budget', v: 'Has to be done. The question is what it says and who signs off.', tag: '' },
      { n: 7, t: 'Leadership development program — 40 leaders enrolled, starts in two weeks', v: 'Positioned as evidence of culture change. Pausing sends a signal.', tag: '' },
      { n: 8, t: 'Harmony device platform migration — vendor support ends September', v: 'If this fails in September it is catastrophic. Will pressure crowd it out?', tag: '' },
    ],
    fields: [
      { id: 'commit',   label: 'Priorities fully committing to (max 3)', hint: 'Name each. Who owns it?' },
      { id: 'manage',   label: 'Priorities managing at reduced capacity (max 3)', hint: 'These continue without executive focus.' },
      { id: 'stop',     label: 'Priority being stopped or paused', hint: 'Deferring is not stopping. Name the consequence and the conversation you owe.' },
      { id: 'tradeoff', label: 'The hardest trade-off — name it honestly', hint: 'Short-term reactive vs long-term stewardship. Priority 4 vs Priority 8.' },
    ],
    steward: [
      'Was this the most honest version of our prioritisation — or the most defensible?',
      'Which stop decision are we least comfortable owning publicly — and why?',
    ],
    dims: [
      'Explicit stop — named consequence + named conversation + named person',
      'Trade-off named honestly — short-term vs long-term tension explicit',
      'Collective signature + Steward Check genuinely completed',
    ],
    bombDim: 'CPO bomb drop — clear honest message: priorities, reasons, timeline',
  },
  {
    name: 'Surface & Escalate',
    eyebrow: 'Round 2 of 3 — MedVance Group',
    dur: 35,
    max: 12,
    sit: 'This information was not in this morning\'s briefing. It has been sitting in a sub-committee report that has not been reviewed at the executive level. A latent defect in the software controlling automated dosage calibration — with the potential to cause device misreading during high-throughput clinical scenarios — was identified by an external reviewer and logged in the risk register 14 months ago. It has never been escalated beyond the Head of Technology. Usage conditions consistent with a failure scenario are forecast within the next two weeks. There is a second issue directly connected to this. The Head of Technology is the only person in MedVance who fully understands this system. They are leaving in six weeks. No knowledge transfer has been planned. This brief asks you to work through four things. They are connected. The order matters.',
    brief: '<strong>Escalation Brief:</strong> (1) Have the honest internal conversation first. Who knew about this, when, and what did they do with it? Establish the facts before you draft anything for external audiences. <br><br>(2) Agree immediate actions. Who owns the technical fix and what is the timeline? Separately — what is the plan for the knowledge transfer before the Head of Technology\'s departure? These are two distinct problems that need two distinct owners. <br><br>(3) Draft three messages: one for the board, one for the TGA/FDA, and one for the person inside the organisation who raised this concern. Each audience needs something different. The message to the board is not the same as the message to the regulator. And neither is the same as what you owe the person who flagged this. <br><br>(4) Diagnose what this situation says about how MedVance manages risk. This is not a one-off oversight. Name the pattern — not the incident. What has to change structurally for this not to happen again? <br><br><strong>WHAT THE BOARD IS WATCHING FOR:</strong> The board wants to see whether this leadership team surfaces risk honestly or manages it quietly. A defect sat in the risk register for 14 months without reaching an executive. The board knows that. What it doesn\’t know yet is whether the pattern has changed. It is watching for strong escalation pathways, clear ownership of the technical fix, a separate owner for the knowledge transfer risk, and honest messaging.',
    bomb: "Someone in the risk team flagged this defect six months ago and was told “it\’s being handled.” This is the second time. She is scared. She is considering going to the TGA. What do you want me to tell her?",
    bombField: {
      label: 'Response to the junior staff member (bomb drop)',
      hint: ' ',
    },
    fields: [
      { id: 'whoknew',   label: 'Who knew, when, and what did they do with it?', hint: 'Have the honest internal conversation first.' },
      { id: 'actions',   label: 'Immediate actions: owner, timeline, knowledge transfer plan', hint: 'Name the fix owner and the KT plan before the Head of Tech departs.' },
      { id: 'board',     label: 'Message to the board', hint: 'Must include the SME dependency and knowledge transfer risk.' },
      { id: 'tga',       label: 'Message to the TGA/FDA', hint: 'Honest about systemic failure. Name the MedVance culture — not just a process gap.' },
      { id: 'diagnosis', label: 'System diagnosis', hint: ' ' },
    ],
    steward: [
      'Does our response to the junior staff member protect her — or manage the risk she represents?',
    ],
    dims: [
      'Board message — includes SME dependency and knowledge transfer risk',
      'TGA/FDA message — honest about systemic failure, names culture not just process',
      'Junior staff member — genuinely protective, acknowledges second raise',
      'System diagnosis — named cultural pattern with leadership implication',
    ],
    bombDim: 'Bomb drop — genuinely protective: did the right thing, no consequences, speak today',
  },
  {
    name: 'The Trade-offs',
    eyebrow: 'Round 3 of 3 — MedVance Group',
    dur: 40,
    max: 12,
    sit: "The board has approved a $100m emergency remediation fund. This is the resource that is available. It is not enough to address the full scope of what needs to be done. Every dollar committed to one area is a dollar not available for another. This brief asks you to make a decision — not a recommendation. The work in front of you this morning has given you the full picture of what MedVance is dealing with: the urgent technical risks, the structural failures that allowed them to develop, and the cultural patterns the external review identified. What follows is the question of how to resource the response. The decision you make here will be the one MedVance takes forward. Name who owns it, name what the organisation is not doing, and be prepared to hold that decision when it is challenged.",
    brief: "<strong>Investment Decision:</strong> (1) Choose one option — A, B, C, D, or a genuinely distinct hybrid. A hybrid must be sequenced and specific. A blend of all four that avoids making a real choice is not a hybrid. <br><br>(2) Name the real reason for this choice. Not the presentable version. <br><br>(3) Name what MedVance is explicitly not doing. Be specific enough that someone could hold you to it at year end. 'Deprioritising some items' is not an answer. <br><br>(4) Map the sequencing. For each major workstream: who owns it, what are they accountable for delivering, and by when. The decision is only as credible as the ownership behind it. <br><br><strong>WHAT THE BOARD IS WATCHING FOR:</strong> The board wants to see how this leadership team works as one. The question is not which option you choose — it is whether the decision is sequenced, owned, and held.",
    bomb: "The board doesn\’t think the team is being ambitious enough. With $100m approved, they want to move faster across all streams. \“I\’m not sure why the leadership team is holding back.\” What are you going to do?",
    bombField: {
      label: "Response to the board's demand for more faster",
      hint: ' ',
    },
    options: [
      { id: 'A', t: 'Option A — Technology First',     d: '70% to APEX system and Harmony platform upgrades. Culture and capability deferred 6 months.',          r: 'TGA/FDA asked for culture evidence. SME capability gap unaddressed.' },
      { id: 'B', t: 'Option B — Balanced Portfolio',   d: 'Roughly equal split: technology 40%, risk & compliance 30%, capability & culture 30%.',                r: 'Nothing gets enough to fully deliver.' },
      { id: 'C', t: 'Option C — External Review First', d: 'Prioritise what the external review called out: risk register, accountability frameworks, engagement.', r: 'Underlying technology problems remain unresolved.' },
      { id: 'D', t: 'Option D — Stop & Sequence',      d: 'Stop Project CLEAR (saves $12m). Pause leadership program. Two streams: APEX upgrade + genuine risk culture reset.', r: 'Disappoints the board (who approved CLEAR) and 40 enrolled leaders.' },
    ],
    fields: [
      { id: 'option',      label: 'Investment option chosen (A, B, C, D, or hybrid)', hint: 'No hedging. A non-committal blend scores 0.' },
      { id: 'realreason',  label: 'The genuine reason for this choice', hint: 'Not the presentable reason. If it could go in a press release unchanged, it is probably not the real reason.' },
      { id: 'notdoing',    label: 'What MedVance is explicitly NOT doing', hint: 'Named initiative + consequence + conversation. Specific enough to hold to at year-end.' },
      { id: 'sequencing',  label: 'Sequencing: who owns each stream, accountable for what, by when', hint: 'Name owner, action, timeline. A list without owners and timelines scores ≤1.' },
    ],
    steward: [
      'Is our sequencing plan specific enough that someone could hold us to it at year-end?',
      "Are we holding the board's position because we genuinely believe it — or because it is easier?",
    ],
    dims: [
      'Decision clarity — actually chose, or hedged?',
      'Real reason — specific, honest, no spin',
      'What MedVance is NOT doing — initiative + consequence + conversation + owners + timelines',
      "Board response — held the line with conviction and evidence",
    ],
    bombDim: 'Bomb drop — held the line with conviction and evidence for why sequencing is right',
  },
];

const DEFAULT_PRIVACY = [
  'We respect your privacy and are committed to protecting your personal information. This application only collects your first name for the purpose of providing and personalising the training experience. Your first name is used solely during the training process and is not shared with any third parties. Once the training has been completed, your first name and any associated personal data are permanently deleted from our systems and are not retained.',
  'We do not sell, store, or use your personal information for marketing purposes.',
  'If you have any questions about how your information is handled, please contact us at movethemachine.com.au or speak with your facilitator.',
].join('\n\n');

const clone = v => JSON.parse(JSON.stringify(v));

/**
 * Effective content — defaults plus any loaded content-pack overlay.
 * These are `let` exports (live bindings): applyContentPack() reassigns them
 * and every importer sees the update. Views must read them at render time
 * (they all do), not cache them at module load.
 */
export let ROUNDS  = clone(DEFAULT_ROUNDS);
export let BADGES  = clone(DEFAULT_BADGES);
export let PRIVACY = DEFAULT_PRIVACY;

const str = (v, fallback) => (typeof v === 'string' && v.trim() ? v : fallback);

/**
 * Overlay a content pack onto the built-in defaults. Text-only by design:
 * the pack can change every human-readable string (narratives, questions,
 * labels, hints, badge names, privacy text, round duration) but never the
 * structure — field ids, field/steward/dim counts, and scoring maxima always
 * come from code. Firebase keys (`subs/{team}_R{n}` field names, score array
 * lengths) therefore cannot be broken from the editor, and a stale or
 * malformed pack degrades to the shipped simulation rather than a crash.
 *
 * Overlays are positional: pack.rounds[1].fields[0] restyles the label/hint
 * of round 2's first field, whose id stays 'whoknew'. Missing entries and
 * empty strings mean "keep the default". Passing null restores defaults.
 *
 * Also applies pack.brand via brand.js (the caller re-runs applyBrand()
 * to push it into the DOM).
 *
 * @param {object|null|undefined} pack
 */
export function applyContentPack(pack) {
  applyBrandOverrides(pack?.brand);
  PRIVACY = str(pack?.privacy, DEFAULT_PRIVACY);

  BADGES = DEFAULT_BADGES.map((b, i) => {
    const o = Array.isArray(pack?.badges) ? pack.badges[i] : null;
    return { ...b, label: str(o?.label, b.label), icon: str(o?.icon, b.icon) };
  });

  ROUNDS = DEFAULT_ROUNDS.map((R, i) => {
    const o = (Array.isArray(pack?.rounds) ? pack.rounds[i] : null) ?? {};
    const round = clone(R);
    for (const k of ['name', 'eyebrow', 'sit', 'brief', 'bomb', 'bombDim']) {
      round[k] = str(o[k], round[k]);
    }
    if (Number.isFinite(o.dur) && o.dur >= 1 && o.dur <= 120) round.dur = Math.round(o.dur);
    round.bombField = {
      label: str(o.bombField?.label, round.bombField.label),
      hint:  str(o.bombField?.hint,  round.bombField.hint),
    };
    round.fields = round.fields.map((f, j) => ({
      ...f,
      label: str(o.fields?.[j]?.label, f.label),
      hint:  str(o.fields?.[j]?.hint,  f.hint),
    }));
    round.steward = round.steward.map((q, j) => str(o.steward?.[j], q));
    round.dims    = round.dims.map((d, j) => str(o.dims?.[j], d));
    if (round.priorities) {
      round.priorities = round.priorities.map((p, j) => ({
        ...p,
        t: str(o.priorities?.[j]?.t, p.t),
        v: str(o.priorities?.[j]?.v, p.v),
      }));
    }
    if (round.options) {
      round.options = round.options.map((opt, j) => ({
        ...opt,
        t: str(o.options?.[j]?.t, opt.t),
        d: str(o.options?.[j]?.d, opt.d),
        r: str(o.options?.[j]?.r, opt.r),
      }));
    }
    return round;
  });
}